import * as React from "react";
import { ChevronDown, X, ArrowUpDown, LayoutGrid, List as ListIcon, Check, SlidersHorizontal } from "lucide-react";
import { cn } from "lib/utils";
import {
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    DropdownCheckboxItem,
    DropdownLabel,
    DropdownSeparator,
} from "components/ui/dropdown";

/* ══════════════════════════════════════════════════════════════════════════
   Toolbar — the strip between a page header and its content.

   Every list page had its own version of this bar: search on the left,
   filters in the middle, sort and a view switch on the right, plus an
   expandable advanced panel. They differed in padding, control height,
   focus treatment and which affordances they exposed. This is that bar,
   once, with a slot API so pages compose rather than re-draw it.
   ══════════════════════════════════════════════════════════════════════════ */

export interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Left region — usually a <SearchInput /> */
    search?: React.ReactNode;
    /** Middle region — filter controls */
    filters?: React.ReactNode;
    /** Right region — sort / view / secondary actions */
    actions?: React.ReactNode;
    /** Applied filters, rendered as removable chips on their own row */
    chips?: React.ReactNode;
    /** Revealed under the main row (advanced filters, bulk-selection bar…) */
    expanded?: React.ReactNode;
    /** Result count / selection summary shown at the far right of the chip row */
    summary?: React.ReactNode;
    /** Drops the card chrome so the toolbar can sit inside another panel */
    bare?: boolean;
}

export const Toolbar: React.FC<ToolbarProps> = ({
    search,
    filters,
    actions,
    chips,
    expanded,
    summary,
    bare = false,
    className,
    children,
    ...props
}) => (
    <div
        role="toolbar"
        aria-label="Filters and view options"
        className={cn(
            "w-full min-w-0",
            !bare && "rounded-xl bg-surface border border-line shadow-card overflow-hidden",
            className
        )}
        {...props}
    >
        <div className="px-3 py-3 sm:px-4 flex flex-col lg:flex-row lg:items-center gap-2.5 min-w-0">
            {search && <div className="flex-1 min-w-0 lg:max-w-xl">{search}</div>}

            {(filters || actions) && (
                <div className="flex items-center gap-2 min-w-0 overflow-x-auto no-scrollbar lg:overflow-visible lg:ml-auto">
                    {filters}
                    {filters && actions && (
                        <span className="hidden lg:block w-px h-6 bg-line flex-shrink-0" aria-hidden="true" />
                    )}
                    {actions}
                </div>
            )}

            {children}
        </div>

        {(chips || summary) && (
            <div className="px-3 sm:px-4 pb-3 -mt-0.5 flex items-center gap-2 flex-wrap min-w-0">
                {chips}
                {summary && (
                    <span className="ml-auto text-meta font-medium text-content-muted tabular-nums flex-shrink-0">
                        {summary}
                    </span>
                )}
            </div>
        )}

        {expanded && <div className="border-t border-line-subtle bg-surface-muted px-3 sm:px-4 py-3.5">{expanded}</div>}
    </div>
);

/* ── Shared control shell ─────────────────────────────────────────────────
   One recipe for every button that lives in a toolbar, so height, radius,
   focus ring and the "active" treatment are identical across pages.       */

const CONTROL_BASE =
    "inline-flex items-center gap-1.5 h-10 px-3 rounded-xl text-ui font-semibold whitespace-nowrap flex-shrink-0 " +
    "border transition-[background-color,border-color,color,box-shadow] duration-150 ease-ds focus-ring " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

const CONTROL_IDLE =
    "bg-surface border-line text-content-secondary hover:bg-surface-hover hover:border-line-strong hover:text-content";

const CONTROL_ACTIVE = "bg-brand-subtle border-brand-border text-brand-on-subtle";

export interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    active?: boolean;
    /** Numeric badge, e.g. count of applied filters */
    count?: number | null;
    icon?: React.ReactNode;
}

export const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
    ({ active, count, icon, className, children, ...props }, ref) => (
        <button
            ref={ref}
            type="button"
            className={cn(CONTROL_BASE, active ? CONTROL_ACTIVE : CONTROL_IDLE, className)}
            {...props}
        >
            {icon}
            {children}
            {count != null && count > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-brand-fg text-2xs font-bold tabular-nums">
                    {count}
                </span>
            )}
        </button>
    )
);
ToolbarButton.displayName = "ToolbarButton";

/* ── FilterMenu ───────────────────────────────────────────────────────── */

export interface FilterOption {
    value: string;
    label: React.ReactNode;
    /** Optional count shown right-aligned */
    count?: number;
    disabled?: boolean;
}

export interface FilterMenuProps {
    label: string;
    icon?: React.ReactNode;
    options: FilterOption[];
    /** Selected values. Single-select uses the first entry. */
    value: string[];
    onChange: (next: string[]) => void;
    multiple?: boolean;
    /** Adds an "All" reset entry at the top */
    allLabel?: string | null;
    align?: "start" | "end";
    className?: string;
}

/**
 * Dropdown filter with a consistent trigger. Multi-select shows a count badge
 * so the applied state is visible without opening the menu.
 */
export const FilterMenu: React.FC<FilterMenuProps> = ({
    label,
    icon = <SlidersHorizontal className="w-3.5 h-3.5" />,
    options,
    value,
    onChange,
    multiple = true,
    allLabel = "All",
    align = "start",
    className,
}) => {
    const selected = value ?? [];
    const isAll = selected.length === 0;

    const toggle = (v: string) => {
        if (!multiple) {
            onChange(selected[0] === v ? [] : [v]);
            return;
        }
        onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    };

    const activeLabel = (() => {
        if (isAll) return label;
        if (!multiple) {
            const found = options.find((o) => o.value === selected[0]);
            return typeof found?.label === "string" ? found.label : label;
        }
        return label;
    })();

    return (
        <Dropdown>
            <DropdownTrigger asChild>
                <ToolbarButton active={!isAll} count={multiple ? selected.length : null} className={className}>
                    {icon}
                    {activeLabel}
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                </ToolbarButton>
            </DropdownTrigger>
            <DropdownContent align={align} className="min-w-[13rem]">
                <DropdownLabel>{label}</DropdownLabel>
                {allLabel && (
                    <>
                        <DropdownCheckboxItem checked={isAll} onCheckedChange={() => onChange([])}>
                            <span className="font-semibold">{allLabel}</span>
                        </DropdownCheckboxItem>
                        <DropdownSeparator />
                    </>
                )}
                {options.map((opt) => (
                    <DropdownCheckboxItem
                        key={opt.value}
                        checked={selected.includes(opt.value)}
                        disabled={opt.disabled}
                        onCheckedChange={() => toggle(opt.value)}
                    >
                        <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                        {opt.count != null && (
                            <span className="ml-2 text-2xs font-semibold text-content-subtle tabular-nums">
                                {opt.count}
                            </span>
                        )}
                    </DropdownCheckboxItem>
                ))}
            </DropdownContent>
        </Dropdown>
    );
};

/* ── FilterChips ──────────────────────────────────────────────────────── */

export interface FilterChip {
    id: string;
    /** e.g. "Status" */
    field?: string;
    /** e.g. "Active" */
    label: React.ReactNode;
    onRemove: () => void;
}

/**
 * Applied filters, shown as removable chips. Making the active filter set
 * visible (and one click to undo) is the fix for "why is this list empty?" —
 * previously the state lived only inside collapsed dropdowns.
 */
export const FilterChips: React.FC<{
    chips: FilterChip[];
    onClearAll?: () => void;
    className?: string;
}> = ({ chips, onClearAll, className }) => {
    if (!chips.length) return null;
    return (
        <div className={cn("flex items-center gap-1.5 flex-wrap min-w-0", className)}>
            {chips.map((chip) => (
                <span
                    key={chip.id}
                    className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-brand-subtle border border-brand-border text-micro font-semibold text-brand-on-subtle max-w-[16rem]"
                >
                    {chip.field && <span className="opacity-70 flex-shrink-0">{chip.field}:</span>}
                    <span className="truncate">{chip.label}</span>
                    <button
                        type="button"
                        onClick={chip.onRemove}
                        className="flex-shrink-0 p-0.5 rounded-full hover:bg-brand-subtle-hover transition-colors focus-ring"
                        aria-label={`Remove filter ${chip.field ? `${chip.field}: ` : ""}${typeof chip.label === "string" ? chip.label : ""}`}
                    >
                        <X className="w-3 h-3" />
                    </button>
                </span>
            ))}
            {onClearAll && chips.length > 1 && (
                <button
                    type="button"
                    onClick={onClearAll}
                    className="h-7 px-2 rounded-full text-micro font-semibold text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring"
                >
                    Clear all
                </button>
            )}
        </div>
    );
};

/* ── SortControl ──────────────────────────────────────────────────────── */

export interface SortOption {
    value: string;
    label: string;
}

export const SortControl: React.FC<{
    options: SortOption[];
    value: string;
    onChange: (value: string) => void;
    direction: "asc" | "desc";
    onDirectionChange: (dir: "asc" | "desc") => void;
    className?: string;
}> = ({ options, value, onChange, direction, onDirectionChange, className }) => {
    const current = options.find((o) => o.value === value);
    return (
        <Dropdown>
            <DropdownTrigger asChild>
                <ToolbarButton className={className} aria-label={`Sort by ${current?.label ?? "field"}`}>
                    <ArrowUpDown className="w-3.5 h-3.5" aria-hidden="true" />
                    <span className="hidden sm:inline">{current?.label ?? "Sort"}</span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                </ToolbarButton>
            </DropdownTrigger>
            <DropdownContent align="end" className="min-w-[12rem]">
                <DropdownLabel>Sort by</DropdownLabel>
                {options.map((opt) => (
                    <DropdownItem key={opt.value} onSelect={() => onChange(opt.value)}>
                        <span className="flex-1">{opt.label}</span>
                        {opt.value === value && <Check className="w-3.5 h-3.5 text-brand" aria-hidden="true" />}
                    </DropdownItem>
                ))}
                <DropdownSeparator />
                <DropdownLabel>Order</DropdownLabel>
                <DropdownItem onSelect={() => onDirectionChange("asc")}>
                    <span className="flex-1">Ascending</span>
                    {direction === "asc" && <Check className="w-3.5 h-3.5 text-brand" aria-hidden="true" />}
                </DropdownItem>
                <DropdownItem onSelect={() => onDirectionChange("desc")}>
                    <span className="flex-1">Descending</span>
                    {direction === "desc" && <Check className="w-3.5 h-3.5 text-brand" aria-hidden="true" />}
                </DropdownItem>
            </DropdownContent>
        </Dropdown>
    );
};

/* ── ViewToggle ───────────────────────────────────────────────────────── */

export interface ViewToggleOption<T extends string = string> {
    value: T;
    label: string;
    icon: React.ReactNode;
}

const DEFAULT_VIEWS: ViewToggleOption[] = [
    { value: "list", label: "List view", icon: <ListIcon className="w-4 h-4" /> },
    { value: "grid", label: "Grid view", icon: <LayoutGrid className="w-4 h-4" /> },
];

/**
 * Segmented list/grid switch. Uses radio semantics so screen readers announce
 * it as one control with a chosen option, not two unrelated buttons.
 */
export function ViewToggle<T extends string = string>({
    value,
    onChange,
    options,
    className,
}: {
    value: T;
    onChange: (value: T) => void;
    options?: ViewToggleOption<T>[];
    className?: string;
}) {
    const opts = (options ?? (DEFAULT_VIEWS as unknown as ViewToggleOption<T>[]));
    return (
        <div
            role="radiogroup"
            aria-label="View mode"
            className={cn(
                "inline-flex items-center p-0.5 rounded-xl bg-surface-sunken border border-line flex-shrink-0",
                className
            )}
        >
            {opts.map((opt) => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-label={opt.label}
                        title={opt.label}
                        onClick={() => onChange(opt.value)}
                        className={cn(
                            "inline-flex items-center justify-center w-9 h-9 rounded-[10px] transition-all duration-150 ease-ds focus-ring",
                            active
                                ? "bg-surface text-brand shadow-xs"
                                : "text-content-muted hover:text-content"
                        )}
                    >
                        {opt.icon}
                    </button>
                );
            })}
        </div>
    );
}

export default Toolbar;
